// Клавиши каркаса (contracts/ui.md, «Палитра ⌘K и «Создать ▾»»; ux.md, раздел 8):
// ⌘K (Ctrl+K) — палитра, / — сразу поиск, ? — список клавиш, [ — скрыть меню, N — «Создать ▾»,
// G затем H, U, J, T — главная, пользователи, журнал, технический журнал; 002 — G затем S —
// «Состояние», G затем J — «Фоновые задачи» у того, кому они открыты (сопровождение): у него J ведёт
// туда, а не в журнал. Esc закрывает верхний слой — это делают сами слои (Radix). Однобуквенные
// клавиши не работают при вводе текста и в открытом окне (ux.md, «Слои и Esc»). Клавиши читаются по
// месту на клавиатуре: в русской раскладке «П», затем «Р» — то же, что G, затем H.
// 032 (T026; FR-002): G, затем I — «Входящие» (в русской раскладке «П», затем «Ш»).
import { CALENDAR_KEYS, altKey, media, modKey } from '@kp/ui';
import { useEffect, useRef } from 'react';

export type GoTarget = 'home' | 'inbox' | 'users' | 'audit' | 'log' | 'status' | 'jobs';

/** Вторая клавиша перехода: G, затем H, I, U, J, T, S. */
export type GoKey = 'h' | 'i' | 'u' | 'j' | 't' | 's';

/** N — открыть «Создать ▾». Подсказка у кнопки и в списке `?`. */
export const CREATE_SHORTCUT = 'N';

/**
 * Протокол во весь экран (ux-dense): ⌘⇧F на Mac, Ctrl+Shift+F на остальных; работают обе.
 * Подпись — в списке `?` и в подсказке кнопки «Во весь экран».
 */
export function focusKeys(): readonly string[] {
  return [modKey(), 'Shift', 'F'];
}

/** Переходы: G, затем H, I, U, J, T, S. Подсказки у пунктов меню, в палитре и в списке `?`. */
export const GO_SHORTCUTS: Readonly<Record<GoTarget, readonly [string, string]>> = {
  home: ['G', 'H'],
  inbox: ['G', 'I'],
  users: ['G', 'U'],
  audit: ['G', 'J'],
  log: ['G', 'T'],
  status: ['G', 'S'],
  jobs: ['G', 'J'],
};

const goPaths: Readonly<Record<string, GoTarget>> = {
  '/': 'home',
  '/inbox': 'inbox',
  '/users': 'users',
  '/audit': 'audit',
  '/ops/log': 'log',
  '/ops/status': 'status',
  '/ops/jobs': 'jobs',
};

/**
 * Куда ведёт G, затем key. J — «Журнал действий», а у того, кому открыты «Фоновые задачи»
 * (jobsOnJ, сопровождение), — «Фоновые задачи»: у одной клавиши один переход.
 */
export function goTargetOf(key: GoKey, jobsOnJ: boolean): GoTarget {
  switch (key) {
    case 'h':
      return 'home';
    case 'i':
      return 'inbox';
    case 'u':
      return 'users';
    case 't':
      return 'log';
    case 's':
      return 'status';
    case 'j':
      return jobsOnJ ? 'jobs' : 'audit';
  }
}

/**
 * Клавиши перехода в раздел по его адресу: «/users» → ['G', 'U']; у раздела без клавиши —
 * undefined. Пункт виден только при праве на раздел, а с правом работает и клавиша. jobsOnJ —
 * G J ведёт в «Фоновые задачи»: у «Журнала действий» клавиши тогда нет.
 */
export function goShortcutFor(
  path: string,
  jobsOnJ = false,
): readonly [string, string] | undefined {
  const target = goPaths[path];
  if (!target) return undefined;
  if (target === (jobsOnJ ? 'audit' : 'jobs')) return undefined;
  return GO_SHORTCUTS[target];
}

export interface ShellKeyActions {
  togglePalette: () => void;
  openSearch: () => void;
  openShortcuts: () => void;
  toggleMenu: () => void;
  /** N: открыть «Создать ▾»; пунктов нет — клавиша не действует. */
  openCreateMenu: () => void;
  /** G, затем key: куда — решает каркас по правам (goTargetOf). */
  go: (key: GoKey) => void;
}

/** Сколько ждать вторую клавишу после G. */
export const SEQUENCE_MS = 1500;

type KeyName = 'mod+k' | '/' | '?' | '[' | 'n' | 'g' | GoKey;

const letters: Readonly<Record<string, KeyName>> = {
  KeyN: 'n',
  KeyG: 'g',
  KeyH: 'h',
  KeyI: 'i',
  KeyU: 'u',
  KeyJ: 'j',
  KeyT: 't',
  KeyS: 's',
};

/** Буквы по значению — латиница и те же клавиши в русской раскладке. */
const letterValues: Readonly<Record<string, KeyName>> = {
  n: 'n',
  т: 'n',
  g: 'g',
  п: 'g',
  h: 'h',
  р: 'h',
  i: 'i',
  ш: 'i',
  u: 'u',
  г: 'u',
  j: 'j',
  о: 'j',
  t: 't',
  е: 't',
  s: 's',
  ы: 's',
};

export function keyName(event: KeyboardEvent): KeyName | null {
  const value = event.key.toLocaleLowerCase('ru-RU');
  if (event.metaKey || event.ctrlKey) {
    return event.code === 'KeyK' || value === 'k' || value === 'л' ? 'mod+k' : null;
  }
  if (event.altKey) return null;
  if (event.key === '?') return '?';
  if (event.key === '/') return '/';
  if (event.key === '[') return '[';
  if (event.code === 'Slash') return event.shiftKey ? '?' : '/';
  if (event.code === 'BracketLeft') return '[';
  return letters[event.code] ?? letterValues[value] ?? null;
}

/** Идёт ввод текста: поле, список, редактируемый текст. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range'].includes(target.type);
  }
  return target.getAttribute('role') === 'combobox' || target.getAttribute('role') === 'textbox';
}

const LAYERS = ['dialog', 'alertdialog', 'menu']
  .map((role) => `[role="${role}"][data-state="open"]`)
  .join(', ');

/**
 * Открыт слой, в котором однобуквенные клавиши не работают: окно, меню, всплывающая панель,
 * меню поверх экрана. Боковая панель записи без затемнения — не окно, пока не на весь экран:
 * до 640 px или развёрнутая («Развернуть», aria-modal).
 */
export function isLayerOpen(): boolean {
  const phone = typeof window.matchMedia === 'function' && window.matchMedia(media.phone).matches;
  return Array.from(document.querySelectorAll(LAYERS)).some(
    (layer) =>
      phone ||
      !layer.classList.contains('kp-drawer') ||
      layer.getAttribute('aria-modal') === 'true',
  );
}

function isGoKey(key: KeyName): key is GoKey {
  return key === 'h' || key === 'i' || key === 'u' || key === 'j' || key === 't' || key === 's';
}

/** Клавиши каркаса на всё окно браузера. */
export function useShellKeys(actions: ShellKeyActions): void {
  const latest = useRef(actions);
  useEffect(() => {
    latest.current = actions;
  });

  useEffect(() => {
    let sequenceAt = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const key = keyName(event);
      if (key === null) {
        sequenceAt = 0;
        return;
      }
      const run = latest.current;
      if (key === 'mod+k') {
        // Палитра открывается и закрывается из любого места, кроме другого окна.
        const palette = document.querySelector('.kp-pal');
        if (!palette && isLayerOpen()) return;
        event.preventDefault();
        run.togglePalette();
        return;
      }
      if (isTypingTarget(event.target) || isLayerOpen()) {
        sequenceAt = 0;
        return;
      }
      const now = Date.now();
      if (sequenceAt > 0 && now - sequenceAt <= SEQUENCE_MS) {
        sequenceAt = 0;
        if (isGoKey(key)) {
          event.preventDefault();
          run.go(key);
        }
        return;
      }
      sequenceAt = 0;
      switch (key) {
        case '/':
          event.preventDefault();
          run.openSearch();
          return;
        case '?':
          event.preventDefault();
          run.openShortcuts();
          return;
        case '[':
          event.preventDefault();
          run.toggleMenu();
          return;
        case 'n':
          event.preventDefault();
          run.openCreateMenu();
          return;
        case 'g':
          sequenceAt = now;
          return;
        case 'h':
        case 'i':
        case 'u':
        case 'j':
        case 't':
        case 's':
          return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}

export interface ShortcutRow {
  /** Шаги: клавиши одного шага нажимаются вместе, шаги — по очереди («G, затем H»). */
  steps: readonly (readonly string[])[];
  /** Другие клавиши того же действия: «Shift F10 или Меню». */
  or?: readonly (readonly (readonly string[])[])[];
  label: string;
}

/** Клавиша контекстного меню на клавиатуре — в списке `?` она подписана так. */
export const CONTEXT_MENU_KEY = 'Меню';

/**
 * Полный список клавиш для `?`: всё, что работает у этого пользователя, и ничего лишнего
 * (contracts/ui.md: «Список `?` полный»). Модификатор — по платформе: ⌘ на Mac, Ctrl на
 * остальных (работают обе). Tab в палитре — только тому, кому есть что искать (canSearch).
 * Клавиши общих компонентов @kp/ui — как ⌘↵ и Esc, всем: Alt+↓ открывает календарь поля даты
 * (DateField), в календаре PageUp и PageDown — месяц, с Shift — год, Home и End — начало и конец
 * недели (CALENDAR_KEYS, T165); Shift+F10 и клавиша меню — меню «…» строки таблицы (DataTable,
 * правый клик с клавиатуры).
 */
export function shortcutRows(options: {
  canCreate: boolean;
  canUsers: boolean;
  canAudit: boolean;
  canSearch: boolean;
  /** Технический журнал (002): право ops.log.read. */
  canLog?: boolean;
  /** 002: «Состояние» (полное или краткое) и «Фоновые задачи». */
  canStatus?: boolean;
  canJobs?: boolean;
  /** «Документы» открыты: клавиши списка папок и протоколов. */
  canDocuments?: boolean;
  /** 032: «Входящие» — у всех с учётной записью; нет — строки G I нет. */
  canInbox?: boolean;
}): ShortcutRow[] {
  const mod = modKey();
  const rows: ShortcutRow[] = [
    { steps: [[mod, 'K']], label: 'Найти или перейти: палитра команд' },
    { steps: [['/']], label: 'Поиск' },
  ];
  if (options.canSearch) {
    rows.push(
      { steps: [['Tab']], label: 'Палитра: переключить команды и поиск' },
      // Backspace в пустом поле поиска возвращает к командам (CommandPalette, contracts/ui.md).
      { steps: [['Backspace']], label: 'Палитра: из пустого поиска — к командам' },
    );
  }
  rows.push(
    { steps: [['?']], label: 'Список клавиш' },
    { steps: [['[']], label: 'Скрыть или показать меню' },
  );
  const go = (target: GoTarget) => GO_SHORTCUTS[target].map((key) => [key]);
  if (options.canCreate) {
    rows.push({ steps: [[CREATE_SHORTCUT]], label: 'Создать: открыть меню «Создать»' });
  }
  rows.push({ steps: go('home'), label: 'Перейти на главную' });
  if (options.canInbox) rows.push({ steps: go('inbox'), label: 'Перейти во «Входящие»' });
  if (options.canUsers) rows.push({ steps: go('users'), label: 'Перейти в «Пользователи»' });
  // G J — один переход: «Фоновые задачи» у сопровождения, иначе «Журнал действий».
  if (options.canAudit && !options.canJobs) {
    rows.push({ steps: go('audit'), label: 'Перейти в «Журнал действий»' });
  }
  if (options.canStatus) rows.push({ steps: go('status'), label: 'Перейти в «Состояние»' });
  if (options.canJobs) rows.push({ steps: go('jobs'), label: 'Перейти в «Фоновые задачи»' });
  if (options.canLog) {
    rows.push({ steps: go('log'), label: 'Перейти в «Технический журнал»' });
  }
  if (options.canDocuments) {
    // «Документы» как Проводник (замечания владельца 24.10.2026).
    rows.push(
      { steps: [['↵']], label: 'Документы: открыть папку или протокол' },
      { steps: [['F2']], label: 'Документы: переименовать' },
      { steps: [['Delete']], or: [[[mod, '⌫']]], label: 'Документы: в корзину' },
      { steps: [[altKey(), '↵']], label: 'Документы: свойства' },
      // Режим фокуса протокола (ux-dense, 25.10.2026): шапка и меню приложения скрыты, выход — Esc.
      { steps: [focusKeys()], label: 'Протокол: во весь экран' },
    );
  }
  rows.push(
    { steps: [[altKey(), '↓']], label: 'Поле даты: открыть календарь' },
    { steps: [CALENDAR_KEYS.previousMonth], label: 'Календарь: предыдущий месяц' },
    { steps: [CALENDAR_KEYS.nextMonth], label: 'Календарь: следующий месяц' },
    { steps: [CALENDAR_KEYS.previousYear], label: 'Календарь: предыдущий год' },
    { steps: [CALENDAR_KEYS.nextYear], label: 'Календарь: следующий год' },
    { steps: [CALENDAR_KEYS.weekStart], label: 'Календарь: начало недели' },
    { steps: [CALENDAR_KEYS.weekEnd], label: 'Календарь: конец недели' },
    {
      steps: [['Shift', 'F10']],
      or: [[[CONTEXT_MENU_KEY]]],
      label: 'Строка таблицы: открыть меню действий',
    },
    { steps: [[mod, '↵']], label: 'Отправить форму в окне' },
    { steps: [['Esc']], label: 'Закрыть верхний слой' },
  );
  return rows;
}
